import * as Sentry from '@sentry/browser';
import { navigate } from 'gatsby';
import debounce from 'lodash/debounce';
import isString from 'lodash/isString';
import take from 'lodash/take';
import { ArrowUpRightIcon, Info, Search, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import {
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/components/ui/command';
import { useAutocomplete } from '@/hooks/useAutocomplete';

import { IS_PRO } from '../../constants';
import { GatsbyContentfulImage } from '../GatsbyContentfulImage';
import { fixSlug } from '../RichText/RichText';
import { DialogFooter } from '../ui/dialog';
import CommandItemComponent from './CommandItemComponent';
import { ExploreCategories } from './ExploreCategories';
import { useCommandMenu } from './useCommandMenu';

type RecentSearch = {
  query: string;
  slug: string;
  time: string;
};

const isRevitOr2DOr3D = (value: string) => {
  const lowerCaseValue = value.toLowerCase();
  return ['rev', '2d', '3d'].some((str) => lowerCaseValue.includes(str));
};

export function CommandMenu() {
  const [recent, setRecent] = useState<RecentSearch[]>([]);
  const [value, setValue] = useState('');
  const { toggle, isOpen } = useCommandMenu();
  const { suggestions, variants, materials, designers } = useAutocomplete({
    query: value,
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [toggle]);

  // Load recent searches from local storage when component mounts
  useEffect(() => {
    const savedRecent = localStorage.getItem('recent-searches');
    if (savedRecent) {
      setRecent(JSON.parse(savedRecent));
    }
  }, []);

  // Save recent searches to local storage whenever it changes
  const handleRecentOnSelect = async (slug: string) => {
    try {
      await navigate(fixSlug(slug));
      toggle();
      setValue('');
    } catch (error) {
      console.error('Navigation failed:', error);
      Sentry.captureException(error);
    }
  };

  const handleExploreOnSelect = async (slug: string) => {
    try {
      await navigate(fixSlug(slug));
      toggle();
      setValue('');
    } catch (error) {
      console.error('Navigation failed:', error);
      Sentry.captureException(error);
    }
  };

  const filteredRecent = recent.filter(({ query }) =>
    query.toLowerCase().includes(value.toLowerCase()),
  );

  const goToSearchPage = async () => {
    try {
      const params = {
        query: value,
      };

      await navigate(`/search?${new URLSearchParams(params)}`);
      toggle();
      setValue('');
    } catch (error) {
      console.error('Navigation failed:', error);
      Sentry.captureException(error);
    }
  };

  const goToMaterialsSearchPage = async () => {
    try {
      const params = {
        query: value,
        'hierarchicalMenu[categories.lvl0]': 'Samples',
      };

      await navigate(`/search?${new URLSearchParams(params)}`);
      toggle();
      setValue('');
    } catch (error) {
      console.error('Navigation failed:', error);
      Sentry.captureException(error);
    }
  };

  const goToDesignersPage = async () => {
    try {
      await navigate(`/designers`);
      toggle();
      setValue('');
    } catch (error) {
      console.error('Navigation failed:', error);
      Sentry.captureException(error);
    }
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={debounce(toggle, 100)}>
      <CommandInput
        placeholder="Search…"
        value={value}
        onValueChange={setValue}
        autoFocus
      />

      <CommandList>
        {IS_PRO && isRevitOr2DOr3D(value) && (
          <CommandGroup
            heading={
              <div className="flex items-center gap-2">
                <Info size={12} />
                Looking for 2D, 3D, or Revit files?
              </div>
            }
            className="divide-y divide-black-100/75"
          >
            <CommandItem value="revit">
              <p className="text-base text-black-600">
                You can find these files on each product's page in the{' '}
                <strong className="text-black-800">Technical details</strong>{' '}
                section. Simply search for the product you're interested in and
                download the files from there!
              </p>
            </CommandItem>
          </CommandGroup>
        )}

        {filteredRecent.length > 0 && (
          <CommandGroup
            heading="Recent"
            className="divide-y divide-black-100/75"
          >
            <div className="divide-y divide-black-50">
              {filteredRecent.map(({ query, slug }) => (
                <CommandItem
                  key={query}
                  value={query}
                  onSelect={() => handleRecentOnSelect(slug)}
                  className="group/recent [&_mark]:text-black flex justify-between gap-4 py-1 text-black-600 hover:cursor-pointer [&_mark]:bg-transparent [&_mark]:font-bold"
                >
                  <span className="line-clamp-1">{query}</span>

                  <div className="flex justify-between gap-2">
                    <button
                      className="group/trash"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        setRecent((prev) =>
                          prev.filter((item) => item.slug !== slug),
                        );
                      }}
                    >
                      <X className="h-4 w-4 text-black-400 group-hover/recent:text-black-600 group-hover/trash:!text-error-600" />
                    </button>
                    <ArrowUpRightIcon className="h-4 w-4 text-black-400 group-hover/recent:text-black-600" />
                  </div>
                </CommandItem>
              ))}
            </div>
          </CommandGroup>
        )}

        {suggestions?.found > 0 && (
          <CommandGroup
            heading={
              value === '' ? 'Popular searches' : 'People also search for'
            }
            className="divide-y divide-black-100/75"
          >
            <div className="divide-y divide-black-50">
              {take(
                suggestions.hits.filter(
                  (suggestion) => suggestion.document.q !== '',
                ),
                5,
              ).map((suggestion) => (
                <CommandItem
                  key={suggestion.document.id}
                  value={suggestion.document.q}
                  onSelect={setValue}
                  className="[&_mark]:text-black group flex justify-between gap-4 py-1 text-black-600 [&_mark]:bg-transparent [&_mark]:font-bold"
                >
                  {isString(suggestion.highlight?.q?.snippet) ? (
                    <span
                      className="line-clamp-1"
                      dangerouslySetInnerHTML={{
                        __html: suggestion.highlight.q.snippet,
                      }}
                    />
                  ) : (
                    <span className="line-clamp-1">
                      {suggestion.document.q}
                    </span>
                  )}
                  <Search className="h-4 w-4 text-black-400 group-hover:text-black-600" />
                </CommandItem>
              ))}
            </div>
          </CommandGroup>
        )}

        {value !== '' && (
          <>
            <CommandGroup
              heading={
                <div className="flex justify-between gap-2">
                  <div>Products</div>
                  <div className="flex gap-2">
                    Showing {(variants?.hits || []).length} of {variants?.found}{' '}
                    results
                    {variants?.found > (variants?.hits || []).length && (
                      <button
                        className="text-black-600 underline underline-offset-1 hover:text-black-800 hover:no-underline"
                        onClick={goToSearchPage}
                      >
                        View all
                      </button>
                    )}
                  </div>
                </div>
              }
              className="divide-y divide-black-100/75"
            >
              {variants?.found > 0 ? (
                <div className="divide-y divide-black-50">
                  {variants?.hits.map((variant) => (
                    <CommandItemComponent
                      key={variant.document.id}
                      {...{
                        variant,
                        toggle,
                        recent,
                        setRecent,
                        setValue,
                      }}
                    />
                  ))}
                  <CommandItem
                    key="no-match-variants"
                    value="no-match-variants"
                    onSelect={goToSearchPage}
                    className="group mb-4 flex gap-4 hover:cursor-pointer"
                  >
                    <Info className="h-4 w-4 text-black-400 group-hover:text-black-600" />
                    <div className="flex-grow">
                      <p className="font-bold">
                        Not the results you’re looking for?
                      </p>
                      <p className="text-black-600 group-hover:text-black-800">
                        View all {variants?.found + materials?.found} results
                        for <em>“{value}”</em>
                      </p>
                    </div>
                    <ArrowUpRightIcon className="h-4 w-4 text-black-400 group-hover:text-black-600" />
                  </CommandItem>
                </div>
              ) : (
                <p className="px-4 pb-2 pt-1.5 text-base text-black-600">
                  No results found
                </p>
              )}
            </CommandGroup>
            <CommandGroup
              heading={
                <div className="flex justify-between gap-2">
                  <div>Material samples</div>

                  <div className="flex gap-2">
                    Showing {(materials?.hits || []).length} of{' '}
                    {materials?.found} results
                    {materials?.found > (materials?.hits || []).length && (
                      <button
                        className="hover:text-black text-black-600 underline hover:no-underline"
                        onClick={goToMaterialsSearchPage}
                      >
                        View all
                      </button>
                    )}
                  </div>
                </div>
              }
              className="divide-y divide-black-100/75"
            >
              {materials?.found > 0 ? (
                <div className="divide-y divide-black-50">
                  {materials?.hits.map((variant) => (
                    <CommandItemComponent
                      key={variant.document.id}
                      {...{
                        variant,
                        toggle,
                        recent,
                        setRecent,
                        setValue,
                      }}
                    />
                  ))}
                  <CommandItem
                    key="no-match-materials"
                    value="no-match-materials"
                    onSelect={goToSearchPage}
                    className="group mb-4 flex justify-between gap-4 hover:cursor-pointer"
                  >
                    <Info className="h-4 w-4 text-black-400 group-hover:text-black-600" />
                    <div className="flex-grow">
                      <p className="font-bold">
                        Not the results you’re looking for?
                      </p>
                      <p className="text-black-600 group-hover:text-black-800">
                        View all {variants?.found + materials?.found} results
                        for <em>“{value}”</em>
                      </p>
                    </div>
                    <ArrowUpRightIcon className="h-4 w-4 text-black-400 group-hover:text-black-600" />
                  </CommandItem>
                </div>
              ) : (
                <p className="px-4 pb-2 pt-1.5 text-base text-black-600">
                  No results found
                </p>
              )}
            </CommandGroup>
            <CommandGroup
              heading={
                <div className="flex justify-between gap-2">
                  <div>Designers</div>
                  <div className="flex gap-2">
                    Showing {(designers?.hits || []).length} of{' '}
                    {designers?.found} results
                    {designers?.found > (designers?.hits || []).length && (
                      <button
                        className="text-black-600 underline underline-offset-1 hover:text-black-800 hover:no-underline"
                        onClick={goToDesignersPage}
                      >
                        View all
                      </button>
                    )}
                  </div>
                </div>
              }
              className="divide-y divide-black-100/75"
            >
              {designers?.found > 0 ? (
                <div className="divide-y divide-black-50">
                  {designers?.hits.map((designer) => (
                    <CommandItem
                      key={designer.document.id}
                      value={designer.document.name}
                      onSelect={async () => {
                        await navigate(fixSlug(designer.document.slug));
                        toggle();
                        setValue('');
                      }}
                      className="[&_mark]:bg-red-matte/60 flex items-start gap-4 hover:cursor-pointer [&_mark]:font-bold"
                    >
                      <GatsbyContentfulImage
                        imageUrl={designer.document.image}
                        alt={designer.document.name}
                        size={64}
                        className="h-16 w-16 rounded-sm mix-blend-multiply"
                      />
                      <div className="flex-1">
                        {isString(designer.highlight?.name?.snippet) ? (
                          <div
                            className="text-black line-clamp-1 text-base font-bold"
                            dangerouslySetInnerHTML={{
                              __html: designer.highlight.name.snippet,
                            }}
                          />
                        ) : (
                          <div className="text-black line-clamp-1 text-base font-bold">
                            {designer.document.name}
                          </div>
                        )}

                        {designer.highlight?.description?.matched_tokens
                          ?.length > 0 ? (
                          <span
                            className="line-clamp-2 text-black-600"
                            dangerouslySetInnerHTML={{
                              __html: designer.document.description.replace(
                                new RegExp(
                                  `(${designer.highlight.description.matched_tokens.join('|')})`,
                                  'gi',
                                ),
                                '<mark>$1</mark>',
                              ),
                            }}
                          />
                        ) : (
                          <span className="line-clamp-2 text-black-600">
                            {designer.document.description}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </div>
              ) : (
                <p className="px-4 pb-2 pt-1.5 text-base text-black-600">
                  No results found
                </p>
              )}
            </CommandGroup>
          </>
        )}

        {value === '' && <ExploreCategories onSelect={handleExploreOnSelect} />}
      </CommandList>

      <DialogFooter className="hidden items-center gap-4 text-sm text-black-600 md:flex">
        <div className="flex items-center gap-1">
          <CommandShortcut>↵</CommandShortcut> to select
        </div>
        <div className="flex items-center gap-1">
          <CommandShortcut>↓</CommandShortcut>
          <CommandShortcut>↑</CommandShortcut> to navigate
        </div>
        <div className="flex items-center gap-1">
          <CommandShortcut className="[font-size:10px]">esc</CommandShortcut> to
          close
        </div>
      </DialogFooter>
    </CommandDialog>
  );
}
